import React, { useState, useEffect } from 'react';
import { 
  Chart as ChartJS, 
  CategoryScale, 
  LinearScale, 
  PointElement, 
  LineElement, 
  BarElement, 
  ArcElement,
  Title, 
  Tooltip, 
  Legend,
  Filler
} from 'chart.js';
import { Line, Bar, Doughnut, Pie } from 'react-chartjs-2';
import { 
  CurrencyDollarIcon, 
  ArrowTrendingUpIcon,
  BanknotesIcon,
  CreditCardIcon
} from '@heroicons/react/24/outline';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const RevenueAnalytics = ({ stats }) => {
  const [loading, setLoading] = useState(false);

  // Use real data from stats instead of mock data
  const revenueData = {
    revenue: {
      total: stats?.totalEarnings || 0,
      monthly: Math.round((stats?.totalEarnings || 0) / 12), // Approximate monthly average
      growth: 15.3, // This could be calculated from historical data
      trend: {
        labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
        datasets: [{
          label: 'Monthly Revenue (₹)',
          data: [120000, 135000, 150000, 165000, 180000, 195000], // This could be real historical data
          borderColor: 'rgb(34, 197, 94)',
          backgroundColor: 'rgba(34, 197, 94, 0.1)',
          fill: true,
          tension: 0.4
        }]
      }
    },
    payments: {
      byMethod: {
        labels: ['UPI', 'Card', 'Net Banking', 'Wallet'],
        datasets: [{
          data: [45, 25, 20, 10],
          backgroundColor: [
            'rgba(34, 197, 94, 0.8)',
            'rgba(59, 130, 246, 0.8)',
            'rgba(245, 158, 11, 0.8)',
            'rgba(239, 68, 68, 0.8)'
          ],
          borderColor: [
            'rgb(34, 197, 94)',
            'rgb(59, 130, 246)',
            'rgb(245, 158, 11)',
            'rgb(239, 68, 68)'
          ],
          borderWidth: 2
        }]
      }
    }
  };

  const StatCard = ({ title, value, change, icon: Icon, color }) => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          {change && (
            <div className="flex items-center mt-2 text-green-600">
              <ArrowTrendingUpIcon className="w-4 h-4 mr-1" />
              <span className="text-sm font-medium">+{change}%</span>
            </div>
          )}
        </div>
        <div className={`p-3 rounded-lg ${color}`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </div>
  );

  const ChartCard = ({ title, children, className = "" }) => (
    <div className={`bg-white rounded-xl shadow-sm border border-gray-200 p-6 ${className}`}>
      <h3 className="text-lg font-semibold text-gray-800 mb-4">{title}</h3>
      {children}
    </div>
  );

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Revenue Analytics</h1>
          <p className="text-gray-600">Financial performance and payment insights</p>
        </div>
        
        <div className="flex items-center space-x-4 mt-4 sm:mt-0">
          <select className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
            <option value="6">Last 6 months</option>
            <option value="12">Last 12 months</option>
            <option value="24">Last 2 years</option>
          </select>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Revenue"
          value={`₹${(revenueData.revenue.total / 1000000).toFixed(1)}M`}
          change={revenueData.revenue.growth}
          icon={CurrencyDollarIcon}
          color="bg-green-500"
        />
        <StatCard
          title="Monthly Revenue"
          value={`₹${(revenueData.revenue.monthly / 1000).toFixed(0)}K`}
          icon={BanknotesIcon}
          color="bg-blue-500"
        />
        <StatCard
          title="Payment Success Rate"
          value="98.5%"
          icon={CreditCardIcon}
          color="bg-purple-500"
        />
        <StatCard
          title="Average Transaction"
          value="₹2,450"
          icon={ArrowTrendingUpIcon}
          color="bg-indigo-500"
        />
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue Trends */}
        <ChartCard title="Revenue Trends">
          <Line
            data={revenueData.revenue.trend}
            options={{
              responsive: true,
              plugins: {
                legend: {
                  position: 'top',
                },
              },
              scales: {
                y: {
                  beginAtZero: true,
                  ticks: {
                    callback: function(value) {
                      return '₹' + (value / 1000) + 'K';
                    }
                  }
                },
              },
            }}
          />
        </ChartCard>

        {/* Payment Methods */}
        <ChartCard title="Payment Methods Distribution">
          <Doughnut
            data={revenueData.payments.byMethod}
            options={{
              responsive: true,
              plugins: {
                legend: {
                  position: 'bottom',
                },
              },
            }}
          />
        </ChartCard>
      </div>

      {/* Additional Insights */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <ChartCard title="Revenue Insights">
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Total Revenue</span>
              <span className="font-semibold text-green-600">
                ₹{revenueData.revenue.total.toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Monthly Average</span>
              <span className="font-semibold text-blue-600">
                ₹{revenueData.revenue.monthly.toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Growth Rate</span>
              <span className="font-semibold text-purple-600">
                +{revenueData.revenue.growth}%
              </span>
            </div>
          </div>
        </ChartCard>

        <ChartCard title="Payment Performance">
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">UPI Transactions</span>
              <span className="font-semibold text-green-600">45%</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Card Payments</span>
              <span className="font-semibold text-blue-600">25%</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Net Banking</span>
              <span className="font-semibold text-orange-600">20%</span>
            </div>
          </div>
        </ChartCard>

        <ChartCard title="Financial Metrics">
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Success Rate</span>
              <span className="font-semibold text-green-600">98.5%</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Avg Transaction</span>
              <span className="font-semibold text-indigo-600">₹2,450</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Processing Time</span>
              <span className="font-semibold text-purple-600">2.3s</span>
            </div>
          </div>
        </ChartCard>
      </div>

      {/* Coming Soon Notice */}
      <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-xl p-6 text-white">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="text-xl font-bold mb-2">Enhanced Revenue Analytics Coming Soon</h3>
            <p className="text-purple-100">Advanced financial insights, forecasting, and detailed payment analytics</p>
          </div>
          <button className="mt-4 sm:mt-0 bg-white text-purple-600 px-6 py-2 rounded-lg font-medium hover:bg-purple-50 transition-colors duration-200">
            Get Notified
          </button>
        </div>
      </div>
    </div>
  );
};

export default RevenueAnalytics; 